/****************************************************
 * Module Name:  userGradePieChart.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useState } from "react";
import DashboardProxy from "../../utility/proxy/manager/dashboardProxy";
import Select from "react-select";
import Constants from "../../utility/constants";
import { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CommonSnackbar from "../common/commonSnackBar";
import Utility from "../../utility/utility";
import { FaUser } from "react-icons/fa";
import { SearchOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

let useColor = Utility.GetRandomColorForPieChart();

const deleteDisableOption = [
  { value: "", label: "-- ANY --" },
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

const UserGradeCount = () => {
  const [chartData, setChartData] = useState([]);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [loader, setLoader] = useState(false);
  const [userType, setUserType] = useState([]);
  const [userTypeOption, setUserTypeOption] = useState([]);
  const [deleted, setDeleted] = useState(deleteDisableOption[0]);
  const [disable, setDisable] = useState(deleteDisableOption[0]);
  const [fetch, setFetch] = useState(false);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setAllUserType();
  }, []);

  useEffect(() => {
    if (fetch) {
      handleSearch();
    }
  }, [fetch]);

  const setAllUserType = () => {
    let optionsArray = [];
    Object.keys(Constants.USER_TYPE).map((key) => {
      let option = { value: key, label: Constants.USER_TYPE[key] };
      optionsArray.push(option);
      return true;
    });
    setUserTypeOption(optionsArray);
    setUserType(
      optionsArray.length > 0 ? optionsArray[0] : userType
    );
    setFetch(true);
  };

  const handleSearch = () => {
    let data = {
      usertype: [userType.value],
      deleted: deleted.value === "" ? [] : [deleted.value],
      disable: disable.value === "" ? [] : [disable.value],
    };
    setLoader(true);
    DashboardProxy.GetUsersGradeCountByUserType(data, (response) => {
      if (response) {
        setChartData(response);
        setLoader(false);
      } else {
        setErrorSnackbar(true);
        setLoader(false);
      }
    });
  };

  function CustomTooltip({ payload, label, active }) {
    if (active) {
      return (
        <Box bgcolor={"#FFFFF7"} padding={2} sx={{ opacity: 0.8 }}>
          <Typography>
            Grade: {payload[0].payload.grade} <br />
            Count: {payload[0].value}
          </Typography>
        </Box>
      );
    }

    return null;
  }

  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />
      <Paper elevation={4}>
        <Card>
          <CardHeader
            avatar={<FaUser size={25} color={useColor[1 % useColor.length]} />}
            title={
              <Typography
                component="span"
                fontWeight={Constants.defaultFontWeight}
                fontSize={"1.25rem"}
              >
                User Bifurcation by Grade
              </Typography>
            }
            subheader="No. of system users by User Type and Grade"
          />
          <CardContent>
            <Box
              sx={{
                display: "flex",
                width: "43vw",
                mb: 3,
                mt: 2,
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px",
                justifyContent: "space-betweeen",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  minWidth: "150px",
                  maxWidth: "auto",
                  gap: "20px",
                }}
              >
                <Box sx={{ alignSelf: "center", mt: 1, width: "75px" }}>
                  <Typography>User Type</Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    minWidth: "150px",
                    maxWidth: "450px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    options={userTypeOption}
                    value={userType}
                    onChange={(e) => setUserType(e)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Deleted
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={deleted}
                    isSearchable={true}
                    name="Deleted"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDeleted(selectedOption)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Disabled
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={disable}
                    isSearchable={true}
                    name="Disabled"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDisable(selectedOption)}
                  />
                </Box>
              </Box>
              <LoadingButton
                loading={loader}
                loadingPosition="start"
                variant="contained"
                onClick={() => handleSearch()}
                startIcon={<SearchOutlined size="large" />}
                sx={{ mt: 1 }}
                disabled={userTypeOption.length === 0}
              >
                Search
              </LoadingButton>
            </Box>
            <Box height={matches ? 300 : 500} width={matches ? 320 : "45vw"}>
              <ResponsiveContainer width="99%">
                <PieChart>
                  <Pie
                    data={chartData}
                    labelLine={true}
                    label
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="grade"
                    stroke="#000000"
                    strokeWidth={1}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={useColor[index % useColor.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </div>
  );
};

export default UserGradeCount;
